import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  Alert,
  Collapse,
  Tooltip,
  Switch,
  CircularProgress,
} from '@mui/material';
import { X, Sparkles, Maximize2, Minimize2, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { FormField, CUSTOMER_VARIABLES } from '../../types/form';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { formEndpoints } from '../../services/api/endpoints/form.endpoint';

const AI_COLOR = '#7C3AED';
const CUSTOMER_VAR_COLOR = '#0D9488';

interface AIPromptConfigDialogProps {
  open: boolean;
  onClose: () => void;
  field: FormField | null;
  onSave: (field: FormField) => void;
  existingFieldIds: string[];
  allFields: FormField[];
  hasVerificationBefore?: boolean;
}

const AIPromptConfigDialog: React.FC<AIPromptConfigDialogProps> = ({
  open,
  onClose,
  field,
  onSave,
  existingFieldIds,
  allFields,
  hasVerificationBefore = false,
}) => {
  const theme = useTheme();
  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );
  const isEditing = !!field;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const optimizationInFlightRef = useRef(false);

  const [fieldName, setFieldName] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [label, setLabel] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [showResultToUser, setShowResultToUser] = useState(true);
  const [error, setError] = useState('');
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);

  // Fields available for reference: non-AI fields + prior AI fields (order < current field's order)
  const currentOrder = field?.order || Infinity;
  const availableFields = allFields.filter(
    f => f.fieldType !== 'ai' || (f.fieldType === 'ai' && f.order < currentOrder)
  );

  useEffect(() => {
    if (open) {
      if (field) {
        setFieldName(field.fieldId);
        setFieldId(field.fieldId);
        setLabel(field.label);
        setAiPrompt(field.aiPrompt || '');
        setShowResultToUser(field.showResultToUser !== false);
      } else {
        setFieldName('');
        setFieldId('');
        setLabel('');
        setAiPrompt('');
        setShowResultToUser(true);
      }
      setError('');
      setPromptExpanded(false);
    }
  }, [open, field]);

  const handleFieldNameChange = (value: string) => {
    setFieldName(value);
    if (!isEditing) {
      const generatedId = value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 30);
      setFieldId(generatedId);
    }
  };

  const insertTextAtCursor = (text: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newPrompt = aiPrompt.substring(0, start) + text + aiPrompt.substring(end);
      setAiPrompt(newPrompt);
      setTimeout(() => {
        textarea.focus();
        const newPos = start + text.length;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      setAiPrompt(prev => prev + text);
    }
  };

  const insertFieldReference = (refFieldId: string) => {
    insertTextAtCursor(`{${refFieldId}}`);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (data) {
      const textarea = textareaRef.current;
      if (textarea) {
        // Insert at current cursor or at end
        const start = textarea.selectionStart;
        const newPrompt = aiPrompt.substring(0, start) + data + aiPrompt.substring(start);
        setAiPrompt(newPrompt);
        setTimeout(() => {
          textarea.focus();
          const newPos = start + data.length;
          textarea.setSelectionRange(newPos, newPos);
        }, 0);
      } else {
        setAiPrompt(prev => prev + data);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const optimizeCurrentPrompt = async (): Promise<string | null> => {
    if (optimizationInFlightRef.current) return null;

    const prompt = aiPrompt.trim();

    if (!prompt) {
      setError('AI Prompt is required');
      return null;
    }
    if (!selectedOrganizationId) {
      setError('No organization selected');
      return null;
    }

    optimizationInFlightRef.current = true;
    setIsOptimizing(true);
    setError('');

    try {
      const result = await formEndpoints.optimizePrompt(selectedOrganizationId, prompt);
      const optimizedPrompt = result?.trim();

      if (!optimizedPrompt) {
        setError('AI optimization returned an empty prompt');
        return null;
      }

      setAiPrompt(optimizedPrompt);
      return optimizedPrompt;
    } catch {
      setError('Failed to optimize prompt');
      return null;
    } finally {
      optimizationInFlightRef.current = false;
      setIsOptimizing(false);
    }
  };

  const handleSave = async () => {
    if (!fieldName.trim()) {
      setError('Field Name is required');
      return;
    }
    if (!fieldId.trim()) {
      setError('Field ID is required');
      return;
    }
    if (showResultToUser && !label.trim()) {
      setError('Label is required');
      return;
    }
    if (!aiPrompt.trim()) {
      setError('AI Prompt is required');
      return;
    }
    if (!isEditing && existingFieldIds.includes(fieldId)) {
      setError('Field ID already exists. Please use a unique identifier.');
      return;
    }

    const optimizedPrompt = await optimizeCurrentPrompt();
    if (!optimizedPrompt) return;

    const savedField: FormField = {
      fieldId: fieldId.trim(),
      label: showResultToUser ? label.trim() : fieldName.trim(),
      fieldType: 'ai',
      required: false,
      aiPrompt: optimizedPrompt,
      showResultToUser,
      order: field?.order || 0,
    };

    onSave(savedField);
  };

  // Render the prompt preview with highlighted field references
  const renderPromptPreview = () => {
    if (!aiPrompt) return null;

    const parts = aiPrompt.split(/(\{\w+\})/g);
    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: theme.palette.action.hover,
          mt: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Preview
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          {parts.map((part, i) => {
            const match = part.match(/^\{(\w+)\}$/);
            if (match) {
              const varId = match[1];
              // Check if it's a customer variable
              const customerVar = CUSTOMER_VARIABLES.find(cv => cv.id === varId);
              if (customerVar) {
                return (
                  <Chip
                    key={i}
                    label={customerVar.label}
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      height: 22,
                      mx: 0.25,
                      backgroundColor: `${CUSTOMER_VAR_COLOR}15`,
                      color: CUSTOMER_VAR_COLOR,
                      borderColor: `${CUSTOMER_VAR_COLOR}40`,
                      verticalAlign: 'middle',
                    }}
                    variant="outlined"
                  />
                );
              }
              const refField = availableFields.find(f => f.fieldId === varId);
              return (
                <Chip
                  key={i}
                  label={refField ? refField.label : varId}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: 22,
                    mx: 0.25,
                    backgroundColor: `${AI_COLOR}15`,
                    color: AI_COLOR,
                    borderColor: `${AI_COLOR}40`,
                    verticalAlign: 'middle',
                  }}
                  variant="outlined"
                />
              );
            }
            return <span key={i}>{part}</span>;
          })}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sparkles size={20} color={AI_COLOR} />
          <Typography variant="h6">{isEditing ? 'Edit AI Prompt' : 'Add AI Prompt'}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Field Name"
            value={fieldName}
            onChange={e => handleFieldNameChange(e.target.value)}
            fullWidth
            required
            disabled={isEditing}
            placeholder="e.g., area_calculation"
            helperText="Internal name for this AI field"
          />

          {showResultToUser && (
            <TextField
              label="Label"
              value={label}
              onChange={e => setLabel(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Calculated Area"
              helperText="The label shown to users for this AI result"
            />
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Show result to user
              </Typography>
              <Typography variant="caption" color="text.secondary">
                When off, the AI result is computed silently and not shown to the user
              </Typography>
            </Box>
            <Switch
              checked={showResultToUser}
              onChange={e => setShowResultToUser(e.target.checked)}
              color="primary"
              size="small"
            />
          </Box>

          {/* Info Panel */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.info.main}30`,
              borderRadius: 1,
              backgroundColor: `${theme.palette.info.main}08`,
            }}
          >
            <Box
              onClick={() => setInfoExpanded(prev => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Info size={16} color={theme.palette.info.main} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: theme.palette.info.main, flex: 1 }}
              >
                Using field references and output formatting
              </Typography>
              {infoExpanded ? (
                <ChevronUp size={14} color={theme.palette.info.main} />
              ) : (
                <ChevronDown size={14} color={theme.palette.info.main} />
              )}
            </Box>
            <Collapse in={infoExpanded}>
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ lineHeight: 1.7 }}
                >
                  Use <code>{'{field_id}'}</code> to insert a field's value into your prompt. If the
                  user skips that field, it will be treated as "not provided" automatically.
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: 0.5,
                    backgroundColor: theme.palette.action.hover,
                    fontFamily: 'monospace',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Recommend a gift for a <strong>{'{age}'}</strong> year old who likes{' '}
                    <strong>{'{hobby}'}</strong>
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ mt: 1.5, lineHeight: 1.7 }}
                >
                  Add <code>[RESULT]:</code> at the end to control the output format.
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: 0.5,
                    backgroundColor: theme.palette.action.hover,
                    fontFamily: 'monospace',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    <strong>[RESULT]:</strong> a single sentence
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ mt: 1.5, lineHeight: 1.7 }}
                >
                  Without <code>[RESULT]</code>, the AI returns a plain text answer.
                </Typography>
              </Box>
            </Collapse>
          </Box>

          {/* Available Fields & Result Format */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: theme.palette.background.default,
            }}
          >
            {/* Section Header */}
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.68rem',
                }}
              >
                Insert into prompt
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25 }}
              >
                Click or drag fields and values into the prompt below
              </Typography>
            </Box>

            {/* Fields List */}
            {availableFields.length > 0 && (
              <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {availableFields.map(f => {
                  const hasOptions =
                    (f.fieldType === 'select' && f.options && f.options.length > 0) ||
                    f.fieldType === 'boolean';
                  const optionLabels =
                    f.fieldType === 'boolean'
                      ? ['Yes', 'No']
                      : (f.options || []).map(o => o.label || o.value);

                  return (
                    <Box
                      key={f.fieldId}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'border-color 0.15s',
                        '&:hover': {
                          borderColor: `${AI_COLOR}60`,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`{${f.fieldId}}`}
                          size="small"
                          draggable
                          onDragStart={e => {
                            e.dataTransfer.setData('text/plain', `{${f.fieldId}}`);
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          onClick={() => insertFieldReference(f.fieldId)}
                          sx={{
                            cursor: 'grab',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            fontSize: '0.72rem',
                            backgroundColor: `${AI_COLOR}10`,
                            color: AI_COLOR,
                            borderColor: `${AI_COLOR}35`,
                            '&:hover': {
                              backgroundColor: `${AI_COLOR}20`,
                              borderColor: AI_COLOR,
                            },
                            '&:active': { cursor: 'grabbing' },
                          }}
                          variant="outlined"
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.72rem' }}
                        >
                          {f.label || f.fieldId}
                        </Typography>
                      </Box>
                      {hasOptions && optionLabels.length > 0 && (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75, ml: 0.5 }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.62rem', alignSelf: 'center', mr: 0.25 }}
                          >
                            values:
                          </Typography>
                          {optionLabels.map(optLabel => (
                            <Chip
                              key={optLabel}
                              label={`"${optLabel}"`}
                              size="small"
                              draggable
                              onDragStart={e => {
                                e.stopPropagation();
                                e.dataTransfer.setData('text/plain', `"${optLabel}"`);
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              onClick={() => insertTextAtCursor(`"${optLabel}"`)}
                              sx={{
                                fontSize: '0.62rem',
                                height: 20,
                                fontFamily: 'monospace',
                                color: theme.palette.text.secondary,
                                borderColor: theme.palette.divider,
                                backgroundColor: 'transparent',
                                cursor: 'grab',
                                '&:hover': {
                                  backgroundColor: `${AI_COLOR}10`,
                                  borderColor: `${AI_COLOR}50`,
                                  color: AI_COLOR,
                                },
                                '&:active': { cursor: 'grabbing' },
                              }}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Result Format Hint */}
            <Box
              sx={{
                mx: 1.5,
                mb: 1.5,
                mt: availableFields.length > 0 ? 0 : 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                backgroundColor: `${AI_COLOR}06`,
                border: `1px dashed ${AI_COLOR}30`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Chip
                label="[RESULT]:"
                size="small"
                draggable
                onDragStart={e => {
                  e.dataTransfer.setData('text/plain', '[RESULT]: ');
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => insertTextAtCursor('[RESULT]: ')}
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  height: 22,
                  backgroundColor: `${AI_COLOR}12`,
                  color: AI_COLOR,
                  borderColor: `${AI_COLOR}40`,
                  cursor: 'grab',
                  '&:hover': {
                    backgroundColor: `${AI_COLOR}22`,
                    borderColor: AI_COLOR,
                  },
                  '&:active': { cursor: 'grabbing' },
                }}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                add at the end to control output format
              </Typography>
            </Box>

            {/* Customer Variables (only if verification is before this field) */}
            {hasVerificationBefore && (
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.68rem',
                    letterSpacing: '0.05em',
                    color: CUSTOMER_VAR_COLOR,
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Customer Variables
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {CUSTOMER_VARIABLES.map(cv => (
                    <Chip
                      key={cv.id}
                      label={cv.placeholder}
                      size="small"
                      draggable
                      onDragStart={e => {
                        e.dataTransfer.setData('text/plain', cv.placeholder);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => insertTextAtCursor(cv.placeholder)}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        backgroundColor: `${CUSTOMER_VAR_COLOR}10`,
                        color: CUSTOMER_VAR_COLOR,
                        borderColor: `${CUSTOMER_VAR_COLOR}35`,
                        cursor: 'grab',
                        '&:hover': {
                          backgroundColor: `${CUSTOMER_VAR_COLOR}20`,
                          borderColor: CUSTOMER_VAR_COLOR,
                        },
                        '&:active': { cursor: 'grabbing' },
                      }}
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.62rem', mt: 0.5, display: 'block' }}
                >
                  Available because this field is after customer verification
                </Typography>
              </Box>
            )}
          </Box>

          {/* Prompt Textarea */}
          <Box onDrop={handleDrop} onDragOver={handleDragOver}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                label="AI Prompt"
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                fullWidth
                required
                multiline
                rows={promptExpanded ? 12 : 4}
                placeholder="e.g., Calculate the area of a rectangle using width {width} and height {height}"
                helperText="Use {fieldId} to reference form field values"
                inputRef={textareaRef}
              />
              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                <Tooltip title="Optimize prompt with AI" arrow placement="top">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!aiPrompt.trim() || isOptimizing}
                      onClick={() => void optimizeCurrentPrompt()}
                      sx={{
                        color: AI_COLOR,
                        bgcolor: 'background.paper',
                        border: `1px solid ${AI_COLOR}30`,
                        '&:hover': { bgcolor: `${AI_COLOR}12`, borderColor: AI_COLOR },
                      }}
                    >
                      {isOptimizing ? (
                        <CircularProgress size={14} sx={{ color: AI_COLOR }} />
                      ) : (
                        <Sparkles size={14} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={() => setPromptExpanded(prev => !prev)}
                  sx={{
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  title={promptExpanded ? 'Collapse' : 'Expand'}
                >
                  {promptExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Prompt Preview */}
          {renderPromptPreview()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isOptimizing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isOptimizing}
          startIcon={isOptimizing ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            backgroundColor: AI_COLOR,
            '&:hover': { backgroundColor: '#6D28D9' },
          }}
        >
          {isOptimizing ? 'Optimizing...' : isEditing ? 'Update AI Field' : 'Add AI Field'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIPromptConfigDialog;
